import { isIOS, isSafari } from "../../../lib/environment";

let __needsHack: boolean | undefined = undefined;
let __vpEl: HTMLElement | undefined = undefined;

export function needsFixedOrAnimatedBgHack(): boolean {
    return isIOS || isSafari;
}

export function needsFixedBgForcedAcceleration(): boolean {
  return isSafari && !isIOS;
}

export function addFixedBgHackAttrToElement(el: HTMLElement) {
  if (el) el.setAttribute("data-fixed-bg-fix", "");
}

export function addScrollAnimatedBgHackAttrToElement(el: HTMLElement) {
  if (el) el.setAttribute("data-sca-bg-fix", "");
}

export function addFixedBgHackForceAccelerationToElement(el: HTMLElement) {
  if (el) el.setAttribute("data-fixed-bg-fix-accel", "");
}

export function applyBgImageToElement(
  el: HTMLElement,
  imgSrc: string,
  isFixed: boolean
) {
  const src = imgSrc ? `url(${imgSrc})` : "";

  if (isFixed && needsFixedOrAnimatedBgHack()) {
    el.style.setProperty("--ps-bg-image", src);
    el.style.removeProperty("background-image");
  } else {
    el.style.backgroundImage = src;
  }
}

export function fixedRefViewport() {
  if (!__vpEl) {
      // we use this element to measure the width/height of vw/vh units, e.g.
      // "the fully expanded viewport" on iOS and Android -> visualViewport,
      // innerHeight, et al., give us the dynamic viewport (e.g. with
      // overlays), and unfortunately there isn't a direct JS-only way to
      // get the "maximum viewport width/height" in pixels otherwise.
      const el = document.createElement("div");

      el.style.width = "100vw";
      el.style.height = "max(100vh, 100%)";
      el.style.marginLeft = "-100000px";
      el.style.position = "fixed";
      el.style.contain = "strict";

      el.setAttribute("inert", "");

      document.body.appendChild(el);

      __vpEl = el;
  }

  const r = __vpEl.getBoundingClientRect();

  return {
    left: 0,
    top: 0,
    width: r.width,
    height: r.height,
    maxWidth: r.width,
    maxHeight: r.height
  };
}

// "fixed" background placement always refers to the positioning within
// the browser viewport. However, we might be constrained in width, so
// we're not filling the viewport, thus picking a (from a user's point
// of view) sub-optimal viewport for the image. We compensate for this
// here by adapting our scale to the available width, rather than the
// viewport width.
export function projectFixedViewportIntoRect(
  vp: TViewportRect,
  rectInViewport: kRect,
  imageSize: [number, number],
  initialScale: number,
  initialPosition: [number, number],
  extraScale = 0
): {
  position: [number, number];
  scale: number;
} {
  let pos = initialPosition;
  let bgScale = (initialScale > 0 ? initialScale : 1) + extraScale;

  const r = rectInViewport;
  const cw = Math.min(1, r.width / vp.maxWidth);

  if (initialScale <= 0) {
    // this is "mode: cover" for us...
    const cas = r.width / vp.maxHeight,
      ias = imageSize[0] / imageSize[1];

    bgScale = Math.max(cw, (ias / cas) * cw) + extraScale;
  } else {
    bgScale = (initialScale + extraScale) * cw;
  }

  // if we're "width-constrained", we need to convert the background
  // position from the browser viewport coordinate system to the more
  // narrow coordinate system of our embed viewport.
  if (cw < 1) {
    // scaled size (in viewport coordinate system) of the image
    const iw = vp.maxWidth * bgScale;

    // constrained area background position, in viewport coords, in pixels
    const cpx = r.left + (r.width - iw) * pos[0];

    // final background position, in percent
    pos = [cpx / (vp.maxWidth - iw), pos[1]];
  }

  return {
    position: pos,
    scale: bgScale,
  };
}



// WEBPACK FOOTER //
// ./lib/pagehandler/utils/fixed-bg-helpers.js